import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { MagicWand, ArrowUUpLeft, PencilSimple, Trash, CircleNotch, WarningCircle, Receipt, UsersThree } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

export default function DraftAssignments() {
  const [jobs, setJobs] = useState([]);
  const [team, setTeam] = useState([]);
  const [reviewing, setReviewing] = useState({});
  const { user } = useAuth();
  const navigate = useNavigate();

  const load = useCallback(() => {
    api.get("/jobs").then((r) => setJobs(r.data)).catch(() => setJobs([]));
    api.get("/team").then((r) => setTeam(r.data)).catch(() => setTeam([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const drafts = jobs.filter((j) => j.status === "draft_prepared");
  const today = new Date().toISOString().slice(0, 10);
  const nameOf = (id) => team.find((t) => t.id === id)?.name || (user && user.id === id ? user.name : null) || "Unassigned";
  const groups = {};
  drafts.forEach((j) => { const k = j.report_writer_id || "unassigned"; (groups[k] = groups[k] || []).push(j); });

  const review = async (j) => {
    setReviewing((m) => ({ ...m, [j.id]: true }));
    try {
      const { data } = await api.post(`/jobs/${j.id}/ai-review`, {}, { timeout: 240000 });
      toast.success(`AI review complete — ${(data.review?.discrepancies || []).length} observation(s). Draft invoice ${data.invoice?.invoice_number || "prepared"}.`);
      load();
    } catch (err) {
      const msg = err.code === "ECONNABORTED" ? "AI review timed out — please try again." : formatApiError(err.response?.data?.detail);
      toast.error(msg);
    } finally { setReviewing((m) => ({ ...m, [j.id]: false })); }
  };
  const reassign = async (j) => {
    try { await api.post(`/jobs/${j.id}/reassign-draft`); toast.success(`${j.reference} sent back to ${nameOf(j.report_writer_id)} for rework`); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const remove = async (j) => {
    try { await api.delete(`/jobs/${j.id}`); toast.success("Job deleted"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="draft-assignments-page">
      <PageHeader overline="04 · Team" title="Draft Assignments" subtitle="Prepared drafts grouped by the report writer. Review with AI before finalising, reassign for rework, edit or delete." />
      {drafts.length === 0 ? (
        <EmptyState icon={UsersThree}>No prepared drafts right now. Jobs appear here when a report writer marks a draft as “Draft Prepared”.</EmptyState>
      ) : Object.entries(groups).map(([wid, list]) => (
        <Card key={wid} className="p-5 border-border" data-testid={`draft-group-${wid}`}>
          <div className="flex items-center gap-2 mb-3">
            <UsersThree size={16} weight="duotone" className="text-accent" />
            <h3 className="font-display font-bold text-lg text-primary">{nameOf(wid === "unassigned" ? null : wid)}</h3>
            <span className="text-xs text-muted-foreground">· {list.length} draft(s)</span>
          </div>
          <div className="space-y-3">
            {list.map((j) => {
              const rev = (j.valuation_details || {}).ai_review;
              const discreps = (rev && rev.discrepancies) || [];
              const dl = (j.deadline || "").slice(0, 10);
              const overdue = dl && dl < today;
              const daysOver = overdue ? Math.round((new Date(today) - new Date(dl)) / 86400000) : 0;
              return (
                <div key={j.id} className="rounded-lg border border-border p-3" data-testid={`draft-job-${j.id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-mono text-xs text-accent">{j.reference}</div>
                      <div className="font-semibold text-sm text-primary truncate">{j.applicant_name || "—"}</div>
                      <div className="text-xs text-muted-foreground truncate">{j.bank_name}{j.branch_name ? ` · ${j.branch_name}` : ""}</div>
                      {dl && <span className={`inline-block mt-1 text-[10px] px-2 py-0.5 rounded-full font-semibold ${overdue ? "bg-red-100 text-red-700" : "bg-muted text-muted-foreground"}`} data-testid={`draft-age-${j.id}`}>{overdue ? `Overdue by ${daysOver} day${daysOver === 1 ? "" : "s"}` : `Due ${dl}`}</span>}
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5">
                      <Button size="sm" className="rounded-full bg-primary hover:bg-[#113A64] text-xs" disabled={reviewing[j.id]} onClick={() => review(j)} data-testid={`draft-review-${j.id}`}>
                        {reviewing[j.id] ? <CircleNotch size={14} className="mr-1 animate-spin" /> : <MagicWand size={14} className="mr-1" />} {reviewing[j.id] ? "Reviewing…" : "Review with AI"}
                      </Button>
                      <Button size="sm" variant="outline" className="rounded-full text-xs" onClick={() => navigate(`/app/jobs?edit=${j.id}`)} data-testid={`draft-edit-${j.id}`}><PencilSimple size={14} className="mr-1" /> Edit</Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild><Button size="sm" variant="outline" className="rounded-full text-xs text-amber-700 border-amber-300" data-testid={`draft-reassign-${j.id}`}><ArrowUUpLeft size={14} className="mr-1" /> Reassign</Button></AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader><AlertDialogTitle>Send draft back for rework?</AlertDialogTitle><AlertDialogDescription>{j.reference} will move back to “Draft Data In” and {nameOf(j.report_writer_id)} will be notified by email/WhatsApp.</AlertDialogDescription></AlertDialogHeader>
                          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => reassign(j)} className="bg-amber-600 hover:bg-amber-700">Reassign</AlertDialogAction></AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                      <AlertDialog>
                        <AlertDialogTrigger asChild><Button size="sm" variant="ghost" className="rounded-full text-xs text-destructive" aria-label="Delete draft" title="Delete draft" data-testid={`draft-delete-${j.id}`}><Trash size={14} /></Button></AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader><AlertDialogTitle>Delete job?</AlertDialogTitle><AlertDialogDescription>Permanently delete {j.reference}? This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
                          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => remove(j)} className="bg-destructive hover:bg-destructive/90">Delete</AlertDialogAction></AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                  {rev && (
                    <div className="mt-3 rounded-md border border-border bg-muted/30 p-3 space-y-2" data-testid={`draft-review-panel-${j.id}`}>
                      <div className="flex flex-wrap items-center gap-2 text-xs">
                        <span className={`px-2 py-0.5 rounded-full font-semibold ${rev.verdict === "ok" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`} data-testid={`draft-verdict-${j.id}`}>{rev.verdict === "ok" ? "Looks consistent" : "Needs changes"}</span>
                        {rev.model && <span className="text-muted-foreground">AI: {rev.model}</span>}
                        {j.ai_review_invoice_id && <span className="inline-flex items-center gap-1 text-emerald-700"><Receipt size={12} /> Draft invoice prepared</span>}
                      </div>
                      {rev.summary && <p className="text-xs text-muted-foreground">{rev.summary}</p>}
                      {discreps.length > 0 ? (
                        <ul className="space-y-1.5">
                          {discreps.map((d, i) => {
                            const sev = (d.severity || "").toLowerCase();
                            const cls = sev === "high" ? "text-red-700" : sev === "medium" ? "text-amber-700" : "text-sky-700";
                            return (
                              <li key={i} className="text-xs flex items-start gap-1.5" data-testid={`draft-discrepancy-${j.id}-${i}`}>
                                <WarningCircle size={13} className={`mt-0.5 shrink-0 ${cls}`} />
                                <span className="flex-1"><b>{d.label || d.field}:</b> report “{d.report_value || "—"}” vs documents “{d.document_value || "—"}”. {d.suggestion && <span className="italic text-muted-foreground">→ {d.suggestion}</span>}</span>
                              </li>
                            );
                          })}
                        </ul>
                      ) : <p className="text-xs text-green-700">No discrepancies found between the documents and the report.</p>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      ))}
    </div>
  );
}
